import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  Session,
  SessionsResponse,
  SessionMessagesResponse,
  GraphDataResponse,
  ChunkSuggestionsResponse,
  ChunkUpdate
} from '../models/chat.models';
import {
  IngestionJob,
  IngestionJobListResponse
} from '../models/ingestion.models';
import {
  FileBrowserListing,
  FilePresignedUploadResponse,
  ConfirmFileUploadRequest,
  ConfirmFileUploadResponse,
  DeleteFileRequest,
  KnowledgeBaseDeleteRequest,
  KnowledgeBaseDeleteResponse,
  MoveFileRequest,
  FileStatusRequest,
  FileStatusEntry,
  FileVersionEntry,
  ParseFilesRequest,
  IngestFilesRequest,
  ParsedFileBrowserListing,
  ParsedFileVersion,
  ParsedFileHighlightsResponse,
  SaveParsedRevisionResponse,
} from '../models/file-browser.models';
import { SKIP_AUTH_HEADER } from '../interceptors/auth.interceptor';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly API_URL = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // Session Management
  createSession(firstMessage: string): Observable<Session> {
    return this.http.post<Session>(`${this.API_URL}/api/sessions/`, { firstMessage })
      .pipe(catchError(this.handleError));
  }
 
  getSessions(): Observable<Session[]> {
    return this.http.get<SessionsResponse>(`${this.API_URL}/api/sessions/`)
      .pipe(
        map(response => response.sessions || []),
        catchError(this.handleError)
      );
  }

  getSessionMessages(id: string): Observable<SessionMessagesResponse> {
    return this.http.get<SessionMessagesResponse>(`${this.API_URL}/api/sessions/${id}/`)
      .pipe(catchError(this.handleError));
  }

  deleteSession(id: string): Observable<any> {
    return this.http.delete(`${this.API_URL}/api/sessions/${id}/`)
      .pipe(catchError(this.handleError));
  }

  // Chat - Note: Streaming is handled separately in the component  
  getChatUrl(): string {
    return `${this.API_URL}/api/chat/`;
  }

  // Graph Data
  getGraphData(statementId: string): Observable<GraphDataResponse> {
    return this.http.post<GraphDataResponse>(`${this.API_URL}/api/knowledge-graph/data/`, { statementId })
      .pipe(catchError(this.handleError));
  }

  getChunkGraphData(chunkId: string): Observable<GraphDataResponse> {
    return this.http.post<GraphDataResponse>(`${this.API_URL}/api/knowledge-graph/data/`, { chunkId })
      .pipe(catchError(this.handleError));
  }

  getAllGraphData(statementIds: string[], chunkIds: string[] = []): Observable<GraphDataResponse> {
    return this.http.post<GraphDataResponse>(`${this.API_URL}/api/knowledge-graph/data/`, { statementIds, chunkIds })
      .pipe(catchError(this.handleError));
  }

  getFullChunkHistory(nodeId: string): Observable<any[]> {
    return this.http.post<any[]>(`${this.API_URL}/api/knowledge-graph/chunk/history/`, { nodeId })
      .pipe(catchError(this.handleError));
  }

  // Chunk Editing
  proposeChunkUpdates(nodeId: string, oldValue: string, nodeLabel: string, desiredChange: string): Observable<ChunkSuggestionsResponse> {
    return this.http.post<ChunkSuggestionsResponse>(
      `${this.API_URL}/api/knowledge-graph/propose-updates/`,
      { nodeId, oldValue, nodeLabel, desiredChange }
    ).pipe(catchError(this.handleError));
  }

  updateChunksAndReingest(updatedChunks: ChunkUpdate[], updatedBy: string = ''): Observable<any> {
    return this.http.post(
      `${this.API_URL}/api/knowledge-graph/chunks/update/`,
      { updatedChunks, updatedBy }
    ).pipe(catchError(this.handleError));
  }

  // Ingestion Jobs
  /**
   * Streams upload events (`UploadProgress` + final `Response`) so callers
   * can drive a progress bar. The X-Skip-Auth header tells the auth and case
   * interceptors to leave the request alone — the presigned URL rejects any
   * unexpected header and the body must be the raw File, not a snake_cased clone.
   */
  uploadFileToS3(uploadUrl: string, file: File): Observable<HttpEvent<unknown>> {
    const headers = new HttpHeaders({
      [SKIP_AUTH_HEADER]: 'true'
    });
    console.log('uploadFileToS3', headers);
    return this.http.put(uploadUrl, file, {
      headers,
      reportProgress: true,
      observe: 'events'
    }).pipe(catchError(this.handleError));
  }

  listIngestionJobs(page: number = 1, pageSize: number = 20): Observable<IngestionJobListResponse> {
    return this.http.get<IngestionJobListResponse | IngestionJob[]>(
      `${this.API_URL}/api/knowledge-graph/ingestion-jobs/`,
      { params: { page: page.toString(), page_size: pageSize.toString() } }
    ).pipe(
      map(response =>
        Array.isArray(response)
          ? { count: response.length, next: null, previous: null, results: response }
          : { count: response.count ?? 0, next: response.next ?? null, previous: response.previous ?? null, results: response.results ?? [] }
      ),
      catchError(this.handleError)
    );
  }

  // File Browser
  listFiles(prefix: string = ''): Observable<FileBrowserListing> {
    const options = prefix ? { params: { prefix } } : {};
    return this.http.get<FileBrowserListing>(
      `${this.API_URL}/api/knowledge-graph/files/`, options
    ).pipe(catchError(this.handleError));
  }

  createFolder(folderPath: string): Observable<void> {
    return this.http.post<void>(
      `${this.API_URL}/api/knowledge-graph/files/folder/`, { folder_path: folderPath }
    ).pipe(catchError(this.handleError));
  }

  deleteFolder(folderPath: string): Observable<void> {
    return this.http.delete<void>(
      `${this.API_URL}/api/knowledge-graph/files/folder/`,
      { body: { folder_path: folderPath } }
    ).pipe(catchError(this.handleError));
  }

  requestFilePresignedUpload(
    s3Key: string, filename: string, sizeBytes: number, contentType?: string
  ): Observable<FilePresignedUploadResponse> {
    return this.http.post<FilePresignedUploadResponse>(
      `${this.API_URL}/api/knowledge-graph/files/presigned-upload/`,
      { s3_key: s3Key, filename, size_bytes: sizeBytes, content_type: contentType }
    ).pipe(catchError(this.handleError));
  }

  confirmFileUpload(req: ConfirmFileUploadRequest): Observable<ConfirmFileUploadResponse> {
    return this.http.post<ConfirmFileUploadResponse>(
      `${this.API_URL}/api/knowledge-graph/files/confirm-upload/`,
      {
        s3_key: req.s3Key,
        version_id: req.versionId,
        filename: req.filename,
        size_bytes: req.sizeBytes,
        parse: req.parse,
        ingest: req.ingest,
      }
    ).pipe(catchError(this.handleError));
  }

  deleteFile(req: DeleteFileRequest): Observable<void> {
    return this.http.delete<void>(
      `${this.API_URL}/api/knowledge-graph/files/delete/`,
      { body: { s3_key: req.s3Key, version_id: req.versionId ?? null } }
    ).pipe(catchError(this.handleError));
  }

  deleteFromKnowledgeBase(req: KnowledgeBaseDeleteRequest): Observable<KnowledgeBaseDeleteResponse> {
    return this.http.delete<KnowledgeBaseDeleteResponse>(
      `${this.API_URL}/api/knowledge-graph/files/knowledge-base/delete/`,
      { body: { s3_key: req.s3Key } }
    ).pipe(catchError(this.handleError));
  }

  getIngestionJob(jobId: string): Observable<IngestionJob> {
    return this.http.get<IngestionJob>(
      `${this.API_URL}/api/knowledge-graph/ingestion-jobs/${jobId}/`
    ).pipe(catchError(this.handleError));
  }

  moveFile(req: MoveFileRequest): Observable<void> {
    return this.http.post<void>(
      `${this.API_URL}/api/knowledge-graph/files/move/`,
      { source_key: req.sourceKey, dest_key: req.destKey }
    ).pipe(catchError(this.handleError));
  }

  getFileStatuses(files: { s3Key: string; versionId: string | null }[]): Observable<FileStatusEntry[]> {
    const body: FileStatusRequest = { files };
    return this.http.post<FileStatusEntry[]>(
      `${this.API_URL}/api/knowledge-graph/files/status/`, body
    ).pipe(catchError(this.handleError));
  }

  getFileVersions(s3Key: string): Observable<FileVersionEntry[]> {
    return this.http.get<{ versions: FileVersionEntry[] }>(
      `${this.API_URL}/api/knowledge-graph/files/versions/`, { params: { s3_key: s3Key } }
    ).pipe(
      map(r => r.versions),
      catchError(this.handleError)
    );
  }

  parseFiles(files: { s3Key: string; versionId: string | null }[]): Observable<{ jobs: { id: string; jobType: string }[] }> {
    const body: ParseFilesRequest = { files };
    return this.http.post<{ jobs: { id: string; jobType: string }[] }>(
      `${this.API_URL}/api/knowledge-graph/files/parse/`, body
    ).pipe(catchError(this.handleError));
  }

  ingestFiles(
    files: { s3Key: string; versionId: string | null; parsedS3Key?: string }[]
  ): Observable<{ jobs: { id: string; jobType: string }[] }> {
    const body: IngestFilesRequest = { files };
    return this.http.post<{ jobs: { id: string; jobType: string }[] }>(
      `${this.API_URL}/api/knowledge-graph/files/ingest/`, body
    ).pipe(catchError(this.handleError));
  }

  // Parsed File Browser
  listParsedFiles(prefix: string = ''): Observable<ParsedFileBrowserListing> {
    const options = prefix ? { params: { prefix } } : {};
    return this.http.get<ParsedFileBrowserListing>(
      `${this.API_URL}/api/knowledge-graph/parsed-files/`, options
    ).pipe(catchError(this.handleError));
  }

  getParsedFileVersions(s3Key: string): Observable<ParsedFileVersion[]> {
    return this.http.get<{ versions: ParsedFileVersion[] }>(
      `${this.API_URL}/api/knowledge-graph/parsed-files/versions/`,
      { params: { s3_key: s3Key } }
    ).pipe(
      map(r => r.versions),
      catchError(this.handleError)
    );
  }

  getParsedFileContentUrl(s3Key: string, versionId?: string | null): Observable<string> {
    const params: any = { s3_key: s3Key };
    if (versionId) params['version_id'] = versionId;
    return this.http.get<{ url: string }>(
      `${this.API_URL}/api/knowledge-graph/parsed-files/content/`, { params }
    ).pipe(
      map(r => r.url),
      catchError(this.handleError)
    );
  }

  getParsedFileHighlights(s3Key: string): Observable<ParsedFileHighlightsResponse> {
    return this.http.get<ParsedFileHighlightsResponse>(
      `${this.API_URL}/api/knowledge-graph/parsed-files/highlights/`,
      { params: { s3_key: s3Key } }
    ).pipe(catchError(this.handleError));
  }

  saveParsedRevision(parsedS3Key: string, content: string): Observable<SaveParsedRevisionResponse> {
    return this.http.post<SaveParsedRevisionResponse>(
      `${this.API_URL}/api/knowledge-graph/parsed-files/revision/`,
      { parsedS3Key, content }
    ).pipe(catchError(this.handleError));
  }

  // Error Handling
  private handleError(error: HttpErrorResponse): Observable<never> {
    console.log('handleError', error);
    let errorMessage = 'An unknown error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      if (error.error?.error) {
        errorMessage = error.error.error;
      } else if (error.error?.details?.message) {
        errorMessage = error.error.details.message;
      } else if (error.error?.details) {
        errorMessage = typeof error.error.details === 'string' 
          ? error.error.details 
          : JSON.stringify(error.error.details);
      } else if (error.message) {
        errorMessage = error.message;
      }
    }
    
    return throwError(() => new Error(errorMessage));
  }
}
